import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import Alert from '@spothero/ui/alert';
import Button from '@spothero/ui/button';
import TextButton from '@spothero/ui/text-button';
import DOMUtils from '@spothero/utils/dom';
import FormatUtils from 'utils/format';
import {push} from 'router/router-actions';
import withConfig from 'config/withConfig';
import TransientMonthlyFacilityDetails from 'common/facility-details/TransientMonthlyFacilityDetails';

class SpotListItemRow extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        spot: PropTypes.object.isRequired,
        isAdmin: PropTypes.bool,
        isMonthly: PropTypes.bool,
        showControls: PropTypes.bool,
        isRebookingReservation: PropTypes.bool,
        isMWSpotCardCTAs: PropTypes.bool,
        cancelAllSpotRequests: PropTypes.func,
        updateSelectedSpot: PropTypes.func,
        onBookSpotClick: PropTypes.func,
        pushTo: PropTypes.func.isRequired,
    };
    state = {
        hovered: false,
    };

    constructor(props) {
        super(props);

        this._IMAGE_WIDTH = props.isMobile ? 75 : 96;
        this._IMAGE_HEIGHT = this._IMAGE_WIDTH;
    }

    componentDidMount() {
        document.addEventListener('spotHover', this._onSpotHover);
    }

    componentWillUnmount() {
        document.removeEventListener('spotHover', this._onSpotHover);
    }

    _onSpotHover = evt => {
        const {
            detail: {hovered, spotId},
        } = evt;

        if (spotId === this.props.spot.spotId) {
            this.setState({
                hovered,
            });
        }
    };

    _onShowDetailsClick = evt => {
        evt.preventDefault();

        const {spot, pushTo} = this.props;

        pushTo(`/spot-details/${spot.spotId}`, {referrer: 'list'});
    };

    _onMouseEnter = () => {
        DOMUtils.sendCustomEvent('spotHover', {
            hovered: true,
            spotId: this.props.spot.spotId,
        });
    };

    _onMouseLeave = () => {
        DOMUtils.sendCustomEvent('spotHover', {
            hovered: false,
            spotId: this.props.spot.spotId,
        });
    };

    _onBookSpotClick = evt => {
        evt.preventDefault();
        evt.stopPropagation();

        const {
            spot,
            cancelAllSpotRequests,
            onBookSpotClick,
            updateSelectedSpot,
        } = this.props;

        if (cancelAllSpotRequests) {
            cancelAllSpotRequests();
        }

        if (onBookSpotClick) {
            onBookSpotClick(spot);
        } else {
            updateSelectedSpot({
                spot,
                referrer: 'list',
                goToSpotUrl: true,
            });
        }
    };

    _renderOfferSchema = () => {
        const {
            spot: {
                selectedRate: {
                    duration,
                    display_price: displayPriceRaw,
                    starts,
                    ends,
                    fullUrl,
                    currency_type: currencyType,
                },
                title,
            },
        } = this.props;
        const {displayPrice} = FormatUtils.price(displayPriceRaw);
        const description = `Reserved Parking at ${title} for ${duration} Hour${
            duration === 1 ? '' : 's'
        }`;

        return (
            <span>
                <meta itemProp="name" content="Parking" />
                <meta itemProp="url" content={fullUrl} />
                <meta itemProp="description" content={description} />
                <meta itemProp="priceCurrency" content={currencyType} />
                <meta itemProp="price" content={displayPrice} />
                <meta
                    itemProp="availability"
                    content="http://schema.org/InStock"
                />
                <meta itemProp="validFrom" content={starts} />
                <meta itemProp="validThrough" content={ends} />
                <span
                    itemProp="offeredBy"
                    itemScope
                    itemType="http://schema.org/Organization"
                >
                    <meta itemProp="name" content="SpotHero" />
                    <meta itemProp="url" content="https://spothero.com" />
                </span>
            </span>
        );
    };

    render() {
        const {
            isMobile,
            spot,
            isMonthly,
            isAdmin,
            isRebookingReservation,
            showControls,
            isMWSpotCardCTAs,
        } = this.props;
        const {
            selectedRate,
            activeMarker,
            spotId,
            available_transient_inventory: inventory,
        } = spot;
        const {hovered} = this.state;
        const {fullUrl, unavailable} = selectedRate;
        const resultType = isMonthly ? 'monthly' : 'hourly';
        const containerClasses = classNames(
            'SpotListItemRow',
            `${resultType}-result`,
            `result${spotId}`,
            {'SpotListItemRow-active': activeMarker},
            {'SpotListItemRow-hovered': hovered},
            {'SpotListItemRow-with-ctas': isMWSpotCardCTAs},
            {unavailable}
        );
        const bookItClasses = classNames('SpotListItemRow-book-it', {
            'admin-border-button': isAdmin && isRebookingReservation,
        });
        const onBookClick = isMonthly ? null : this._onBookSpotClick;
        const showDetailsBtn =
            (!isMonthly && !isMWSpotCardCTAs) ||
            (isMonthly && isMWSpotCardCTAs);
        const showBookBtn =
            !isMobile || (isMobile && !isMonthly && isMWSpotCardCTAs);

        return (
            <div
                className={containerClasses}
                data-spot={spotId}
                itemProp="offers"
                itemScope
                itemType="http://schema.org/Offer"
                onMouseEnter={this._onMouseEnter}
                onMouseLeave={this._onMouseLeave}
                onClick={this._onShowDetailsClick}
            >
                {this._renderOfferSchema()}
                <div
                    itemProp="availableAtOrFrom"
                    itemScope
                    itemType="http://schema.org/ParkingFacility"
                >
                    <TransientMonthlyFacilityDetails
                        spot={spot}
                        isMonthly={isMonthly}
                        imageWidth={this._IMAGE_WIDTH}
                        imageHeight={this._IMAGE_HEIGHT}
                    />
                </div>
                {showControls && (
                    <div className="controls">
                        {showDetailsBtn && (
                            <TextButton className="show-details">
                                {isMWSpotCardCTAs ? 'View ' : ''}Details
                            </TextButton>
                        )}
                        {unavailable ? (
                            <Button color="primary" itemProp="url" disabled>
                                Unavailable <sup className="text-light">*</sup>
                            </Button>
                        ) : (
                            showBookBtn && (
                                <TextButton
                                    className={bookItClasses}
                                    href={fullUrl}
                                    color="primary"
                                    itemProp="url"
                                    onClick={onBookClick}
                                >
                                    {isRebookingReservation
                                        ? 'Rebook It'
                                        : 'Book Now'}
                                </TextButton>
                            )
                        )}
                    </div>
                )}
                {inventory > 0 && inventory < 4 && (
                    <Alert
                        className="remaining-spots"
                        type="highlight"
                        size="sm"
                    >
                        {inventory} {`spot${inventory > 1 ? 's' : ''}`} left
                    </Alert>
                )}
            </div>
        );
    }
}

const mapDispatchToProps = {
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(
        null,
        mapDispatchToProps
    )
);

export default enhance(SpotListItemRow);
